import React, { useEffect, useState , useMemo} from 'react';

import classNames from 'classnames';
import {
    Area,
    CartesianGrid,
    ComposedChart,
    DotProps,
    Line,
    ResponsiveContainer,
    Tooltip,
    TooltipProps,
    XAxis,
    YAxis,
} from 'recharts';

import {
    DataInfo,
    IDataInfoProps,
} from '../data-info';
import {
    breakPoints,
    debounce,
    getColorByCode,
    TColorCode,
    TBreak,
} from '../helpers';

export interface IChartProps {
    aspect: number;
    data: IChartDataProps[] | IDataInfoProps;
    id: string;
    areas?: IChartLayerProps[];
    className?: string;
    hideDots?: boolean;
    hideGrid?: boolean;
    hideXAxis?: boolean;
    hideYAxis?: boolean;
    lines?: IChartLayerProps[];
    showTooltip?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tickFormatter?: (value: any, index: number) => string;
    tooltipFormatter?: (index: number, data: IChartTooltipDataPoint[]) => JSX.Element;
}

export interface IChartTooltipDataPoint {
    index: number;
    name?: string;
    value?: number;
}

export interface IChartDataProps {
    /**
     * Array of Y-axis coordinates.
     */
    [key: string]: number | undefined;
    /**
     * X-axis coordinate.
     */
    name: number;
}

export interface IChartLayerProps {
    key: string;
    color?: TColorCode;
    strokePattern?: string;
    strokeWidth?: number;
    lineColor?: TColorCode;
}

export interface IChartTooltipProps extends TooltipProps<number, string> {
    isVisible: boolean;
    tooltipFormatter?: (index: number, data: IChartTooltipDataPoint[]) => JSX.Element;
}

const ChartTooltip = (props: IChartTooltipProps): JSX.Element | null => {
    const { active, payload } = props;
    const className: string = classNames(
        'chart__tooltip',
        {
            'is-visible': props.isVisible,
        },
    );

    if (active && payload && payload.length > 0) {
        const data: IChartTooltipDataPoint[] = [];

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        payload.map((item: any, i: number) => {
            data.push({
                index: i,
                name: item.name,
                value: item.value,
            });
        });

        if (props.tooltipFormatter) {
            return (
                <div className={className}>
                    {props.tooltipFormatter(props.label, data)}
                </div>
            );
        } else {
            return (
                <div className={className}>
                    <div className="text">
                        <p>{props.label}</p>
                        <p>{JSON.stringify(data)}</p>
                    </div>
                </div>
            );
        }
    }

    return null;
};

export interface IChartActiveDotProps extends DotProps {
    onShow?: (x: number, y: number) => void;
    onHide?: () => void;
}

const ChartActiveDot = (props: IChartActiveDotProps): JSX.Element | null => {
    const { cx, cy } = props;

    React.useEffect(() => {
        if (cx && cy) {
            if (props.onShow) {
                props.onShow(cx, cy);
            }
        } else {
            if (props.onHide) {
                props.onHide();
            }
        }
    }, [cx, cy]);

    if (cx && cy) {
        return (
            <g>
                <circle
                    cx={cx}
                    cy={cy}
                    fill='#ffffff'
                    fillOpacity={1}
                    r={12}
                    stroke="#1c2d41"
                    strokeWidth={2}
                />
                <circle
                    cx={cx}
                    cy={cy}
                    fill="#ff7d51"
                    r={4}
                />
            </g>
        );
    }

    return null;
};

const Chart = ({
    aspect = 2.2,
    className,
    data,
    id,
    hideDots,
    hideGrid,
    hideXAxis,
    hideYAxis,
    showTooltip,
    tickFormatter,
    tooltipFormatter,
    areas,
    lines,
}: IChartProps): JSX.Element => {
    const [dotPosition, setDotPosition] = useState<{ x: number | undefined, y: number | undefined }>({ x: undefined, y: undefined });
    const [windowWidth, setWindowWidth] = useState<TBreak>('xs');

    const onResize = () => {
        if (window.matchMedia(`(min-width: ${breakPoints.xl}px)`).matches) {
            setWindowWidth('xl');
        } else if (window.matchMedia(`(min-width: ${breakPoints.lg}px)`).matches) {
            setWindowWidth('lg');
        } else if (window.matchMedia(`(min-width: ${breakPoints.md}px)`).matches) {
            setWindowWidth('md');
        } else if (window.matchMedia(`(min-width: ${breakPoints.sm}px)`).matches) {
            setWindowWidth('sm');
        } else {
            setWindowWidth('xs');
        }
    };
    const debouncedResizeHandler = useMemo(() => debounce(onResize, 50), []);

    useEffect(() => {
        onResize();
        window.addEventListener('resize', debouncedResizeHandler);

        return () => {
            window.removeEventListener('resize', debouncedResizeHandler);
            debouncedResizeHandler.clear();
        };
    }, [debouncedResizeHandler]);

    const computedClassName = classNames('chart', className);

    const hasChartData = (chartData: IChartDataProps[] | IDataInfoProps): chartData is IChartDataProps[] => Array.isArray(chartData) && chartData.length > 0;

    const renderAreas = useMemo(() => (
        (areas || []).map((area, i) => (
            <Area
                key={`area-${i}`}
                activeDot={
                    <ChartActiveDot
                        onShow={(x, y) => {
                            if (x !== dotPosition.x || y !== dotPosition.y) {
                                setDotPosition({ x, y });
                            }
                        }}
                        onHide={() => setDotPosition({ x: undefined, y: undefined })}
                    />
                }
                connectNulls
                dataKey={area.key}
                dot={!hideDots ? {
                    fill: '#ffffff',
                    fillOpacity: '1',
                    r: windowWidth === 'xs' ? 3 : 5,
                    stroke: '#1c2d41',
                    strokeWidth: '2px',
                } : false}
                fill={area.color ? getColorByCode(`${area.color}` as TColorCode) : '#d5fde3'}
                fillOpacity={0.5}
                stroke={area.lineColor ? getColorByCode(area.lineColor) : '#1c2d41'}
                strokeWidth={area.strokeWidth || 2}
                type="monotone"
                xAxisId="x1"
                yAxisId="y1"
            />
        ))
    ), [areas, dotPosition, hideDots, windowWidth]);

    const onMouseEnter = (dotProps: DotProps) => {
        const { cx, cy } = dotProps;

        if (cx && cy && (dotPosition.x !== cx || dotPosition.y !== cy)) {
            setDotPosition({ x: cx, y: cy });
        }
    };

    const renderLines = useMemo(() => (
        (lines || []).map((line, i) => (
            <Line
                key={`line-${i}`}
                activeDot={{ onMouseEnter }}
                connectNulls
                dataKey={line.key}
                dot={!hideDots ? {
                    fill: '#bec4cd',
                    fillOpacity: '1',
                    r: 3,
                    stroke: 'transparent',
                    strokeWidth: 0,
                    strokeDasharray: 0,
                } : false}
                stroke={getColorByCode(line.color!)}
                strokeWidth={line.strokeWidth}
                strokeDasharray={line.strokePattern}
                type="monotone"
                xAxisId="x1"
                yAxisId="y1"
            />
        ))
    ), [lines, onMouseEnter, hideDots]);

    return hasChartData(data) ? (
        <ResponsiveContainer id={id} aspect={aspect} className={computedClassName} debounce={20} width="100%">
            <ComposedChart
                data={data}
                margin={{
                    top: hideDots ? 5 : 15,
                    right: hideDots ? 0 : 15,
                    bottom: hideXAxis ? 0 : 10,
                    left: 0,
                }}
            >
                {!hideGrid && <CartesianGrid horizontal stroke="#f6f7f8" strokeWidth={1} vertical={false} />}
                {areas && renderAreas}
                {lines && renderLines}
                {!hideXAxis && (
                    <XAxis
                        axisLine={{ stroke: '#2dca73', strokeWidth: 0.5 }}
                        dataKey="name"
                        domain={['dataMin', 'dataMax']}
                        tickCount={data.length !== 0 ? data[data.length - 1].name - data[0].name + 1 : 0}
                        tick={{ fontSize: '12px', transform: 'translate(0, 20)' }}
                        tickFormatter={tickFormatter}
                        tickLine={false}
                        type="number"
                        xAxisId="x1"
                    />
                )}
                {!hideYAxis && (
                    <YAxis
                        axisLine={false}
                        domain={[0, 100]}
                        tick={{ fontSize: '12px', transform: 'translate(-15, 0)' }}
                        tickLine={false}
                        type="number"
                        unit="%"
                        yAxisId="y1"
                    />
                )}
                {showTooltip && (
                    <Tooltip
                        allowEscapeViewBox={{ x: true, y: true }}
                        cursor={(dotPosition.x && dotPosition.y) ? undefined : false}
                        content={<ChartTooltip isVisible={!!(dotPosition.x && dotPosition.y)} tooltipFormatter={tooltipFormatter} />}
                        isAnimationActive={false}
                        position={dotPosition}
                    />
                )}
            </ComposedChart>
        </ResponsiveContainer>
    ) : (
        <DataInfo {...data} />
    );
};

export default Chart;
